import { useId } from 'react';
import { useField } from 'formik';
import { TextField, type TextFieldProps } from '@mui/material';

export interface A11yFormFieldProps extends Omit<TextFieldProps, 'name' | 'value' | 'onChange' | 'error' | 'helperText'> {
  /** Formik field name. */
  name: string;
  /** Visible label. */
  label: string;
  /** Show a required asterisk (MUI `required` prop). */
  required?: boolean;
  /** Override the helper text rendered when the field is valid. */
  helperText?: string;
}

/**
 * Drop-in Formik TextField that guarantees consistent aria wiring:
 * - visible `<label>` (MUI `label` prop)
 * - visible required asterisk via MUI `required`
 * - `aria-invalid` driven by Formik's `touched && error`
 * - `aria-describedby` pointing at the helper/error element so assistive tech
 *   announces the message when the field is focused
 */
export default function A11yFormField({ name, label, required, helperText, ...rest }: A11yFormFieldProps) {
  const [field, meta] = useField<unknown>(name);
  const helperId = useId();

  const hasError = Boolean(meta.touched && meta.error);
  const displayedHelper = hasError ? String(meta.error) : helperText;

  return (
    <TextField
      {...rest}
      {...field}
      label={label}
      required={required}
      error={hasError}
      helperText={displayedHelper}
      slotProps={{
        ...rest.slotProps,
        input: {
          ...rest.slotProps?.input,
          'aria-invalid': hasError ? true : undefined,
          'aria-describedby': displayedHelper ? helperId : undefined
        },
        formHelperText: {
          ...rest.slotProps?.formHelperText,
          id: helperId
        }
      }}
    />
  );
}
